import type { Notification } from '@/types'

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : undefined
}

export function getNotificationPrimaryAction(n: Notification): string | undefined {
  return str(n.metadata?.primaryAction)
}

export function getNotificationActionStatus(n: Notification): string | undefined {
  return str(n.metadata?.actionStatus)
}

export function isConfirmShiftActionAvailable(n: Notification): boolean {
  return (
    getNotificationPrimaryAction(n) === 'confirm_shift' &&
    getNotificationActionStatus(n) !== 'expired'
  )
}

export function isReadOnlyShiftAction(n: Notification): boolean {
  const status = getNotificationActionStatus(n)
  return status === 'expired' || status === 'confirmed' || status === 'cancelled'
}

export function isShiftAlertNotification(n: Notification): boolean {
  if (n.type === 'SHIFT_EXTENDED') return false
  return (
    n.type === 'SHIFT_ALERT' ||
    n.type === 'SHIFT_ASSIGNED' ||
    n.reference?.type === 'shift_alert' ||
    n.reference?.type === 'shift_assignment' ||
    getNotificationPrimaryAction(n) === 'confirm_shift'
  )
}

export interface ShiftAlertExtractedContext {
  companyId?: string
  shiftAssignmentId?: string
  alertId?: string
  shiftsPlanningPath?: string
}

/**
 * Reads company/assignment from notification reference, metadata (camel or snake_case),
 * or parses in-app deep link from reference.url.
 */
export function extractShiftAlertContext(n: Notification): ShiftAlertExtractedContext {
  const ref = n.reference
  const meta = n.metadata ?? {}
  const companyId =
    str(ref?.companyId) ?? str(meta.companyId) ?? str(meta.company_id)
  const shiftAssignmentId =
    str(ref?.shiftAssignmentId) ??
    str(meta.shiftAssignmentId) ??
    str(meta.shift_assignment_id)
  const alertId =
    ref?.type === 'shift_alert' && ref.id ? ref.id : n.type === 'SHIFT_ALERT' ? ref?.id : undefined

  let shiftsPlanningPath: string | undefined
  let urlCompanyId: string | undefined
  let urlAssignmentId: string | undefined
  if (ref?.url) {
    try {
      const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
      const u = new URL(ref.url, base)
      const path = `${u.pathname}${u.search}`
      const isShiftsPath =
        u.pathname === '/shifts' ||
        u.pathname.startsWith('/shifts/') ||
        (path.includes('/companies/') && path.includes('/shifts'))
      if (isShiftsPath) {
        shiftsPlanningPath = path.startsWith('/') ? path : `/${path}`
        urlCompanyId = str(u.searchParams.get('companyId'))
        urlAssignmentId =
          str(u.searchParams.get('shiftAssignmentId')) ?? str(u.searchParams.get('assignment'))
      }
    } catch {
      /* ignore invalid url */
    }
  }
  const finalCompanyId = companyId ?? urlCompanyId
  const finalAssignmentId = shiftAssignmentId ?? urlAssignmentId
  if (!shiftsPlanningPath && finalCompanyId && finalAssignmentId) {
    shiftsPlanningPath = `/shifts?companyId=${encodeURIComponent(finalCompanyId)}&shiftAssignmentId=${encodeURIComponent(finalAssignmentId)}`
  }
  return {
    companyId: finalCompanyId,
    shiftAssignmentId: finalAssignmentId,
    alertId,
    shiftsPlanningPath,
  }
}

export function assignmentIdFromShiftsPath(path: string): string | undefined {
  try {
    const u = new URL(path, 'http://localhost')
    const a = u.searchParams.get('shiftAssignmentId') ?? u.searchParams.get('assignment')
    return a && a.length > 0 ? a : undefined
  } catch {
    return undefined
  }
}

export function companyIdFromShiftsPath(path: string): string | undefined {
  const m = path.match(/\/companies\/([^/]+)\/shifts/)
  if (m?.[1]) return m[1]
  try {
    const u = new URL(path, 'http://localhost')
    const c = u.searchParams.get('companyId')
    return c && c.length > 0 ? c : undefined
  } catch {
    return undefined
  }
}
